import React, { useCallback, useMemo, useState } from 'react';
import './styles.css';
import NavRail from './NavRail';
import { TweaksPanel, TweakSection, TweakRadio, TweakToggle, useTweaks } from './TweaksPanel';
import { mergeDefaults } from './themePersist';
import { CommentsRail, AuditRail } from './Panels';
import { ALL_ROWS, COUNTS, TABLES, fmt, ReportRow, ReportTable, RowStatus } from './data';

const TWEAK_DEFAULTS = {
  theme: 'paper' as 'paper' | 'light' | 'dark',
  density: 'regular' as 'compact' | 'regular' | 'comfy',
  showComments: true,
  showAudit: true,
  variant: 'safe' as 'safe' | 'bold',
};

interface Counts { match: number; diff: number; over: number; none: number }

function TitleBar({ total }: { total: number }) {
  return (
    <div className="titlebar">
      <span className="brand">XELENCE</span>
      <span className="sep">/</span>
      <span className="crumb">Reports</span>
      <span className="sep">/</span>
      <span className="crumb"><b>Delta Doors</b> · Q1 2026</span>
      <div className="right">
        <div className="stat"><span className="k">cluster</span><span className="v">dd_1quart_2026</span></div>
        <div className="stat"><span className="k">config</span><span className="v">full_sgd_v3</span></div>
        <div className="stat"><span className="k">computed</span><span className="v">29 апр 11:42</span></div>
        <div className="stat"><span className="k">rows</span><span className="v">{total}</span></div>
      </div>
    </div>
  );
}

function Switcher() {
  return (
    <div className="switcher">
      <div className="sw" title="Cmd-K">
        <div>
          <div className="lbl">CLUSTER</div>
          <div className="val">dd · 1 кв. 2026<span className="meta">8/8 файлов</span></div>
        </div>
        <div className="caret">▾</div>
      </div>
      <div className="sw">
        <div>
          <div className="lbl">CONFIG</div>
          <div className="val">full_sgd_v3<span className="meta">3 таблицы · 41 строка</span></div>
        </div>
        <div className="caret">▾</div>
      </div>
      <div className="sw" style={{ borderRight: 0 }}>
        <div>
          <div className="lbl">MASTER</div>
          <div className="val">master_2026_q1.xlsx<span className="meta">v.4 · 28.04</span></div>
        </div>
        <div className="caret">▾</div>
      </div>
      <div className="actions">
        <button className="iconbtn">↻ Recompute</button>
        <button className="iconbtn">⤓ Export</button>
        <button className="iconbtn">⎙ Print<span className="kbd">⌘P</span></button>
      </div>
    </div>
  );
}

const FILTERS: { id: 'all' | RowStatus; label: string; dotCls: string }[] = [
  { id: 'all',   label: 'Все',           dotCls: '' },
  { id: 'diff',  label: 'Расхождения',   dotCls: 'is-diff' },
  { id: 'match', label: 'Совпадения',    dotCls: 'is-match' },
  { id: 'over',  label: 'Корректировки', dotCls: 'is-over' },
];

interface DiffBarProps {
  filter: 'all' | RowStatus;
  setFilter: (v: 'all' | RowStatus) => void;
  counts: Counts;
  query: string;
  setQuery: (v: string) => void;
  onJumpFirstDiff: () => void;
}

function DiffBar({ filter, setFilter, counts, query, setQuery, onJumpFirstDiff }: DiffBarProps) {
  return (
    <div className="diffbar">
      {FILTERS.map((f) => {
        const n = f.id === 'all'
          ? (counts.match + counts.diff + counts.over + counts.none)
          : counts[f.id];
        return (
          <button
            key={f.id}
            className={`pill ${f.dotCls}`}
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.dotCls && <span className="dot" />}
            <span>{f.label}</span>
            <span className="n">{n}</span>
          </button>
        );
      })}
      <div className="summary">
        {counts.diff > 0 ? (
          <>
            <span><b>{counts.diff}</b> строк расходятся с master</span>
            <button className="jump" onClick={onJumpFirstDiff}>jump → first ↵</button>
          </>
        ) : (
          <span style={{ color: 'var(--match)' }}>● все строки совпадают с master</span>
        )}
        {counts.over > 0 && (
          <span style={{ color: 'var(--ink-3)' }}>· <b style={{ color: 'var(--override)' }}>{counts.over}</b> ручных корректировок</span>
        )}
      </div>
      <div className="search">
        <span style={{ color: 'var(--ink-4)', fontFamily: 'var(--mono)', fontSize: 11 }}>⌕</span>
        <input
          placeholder="найти по строке…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="kbd">/</span>
      </div>
    </div>
  );
}

function Tree({ row }: { row: ReportRow }) {
  const srcs = row.sources || [];
  if (srcs.length === 0 && !row.formula) {
    return (
      <div className="tree">
        <div className="tree-row">
          <div></div>
          <div><span className="lines">└─</span><span className="desc" style={{ marginLeft: 8 }}>прямой источник не задан · значение задано константой</span></div>
          <div></div><div></div><div></div><div></div>
        </div>
      </div>
    );
  }
  return (
    <div className="tree">
      {srcs.map((s, i) => {
        const last = i === srcs.length - 1 && !row.formula;
        return (
          <div key={i} className="tree-row">
            <div></div>
            <div>
              <span className="lines">{last ? '└─' : '├─'}</span>
              <span className="src" style={{ marginLeft: 8 }}>
                <span className="file">{s.file}</span>
                <span className="cell">!{s.cell}</span>
              </span>
              <span className="desc">{s.desc}</span>
            </div>
            <div className="c-num">{fmt(s.value)}</div>
            <div className="c-master">—</div>
            <div className="c-diff">—</div>
            <div></div>
          </div>
        );
      })}
      {row.formula && (
        <div className="tree-foot">
          <div></div>
          <div>
            <span className="lines" style={{ marginRight: 8 }}>└─</span>
            FORMULA <span style={{ color: 'var(--ink-2)', fontFamily: 'var(--mono)', textTransform: 'none', letterSpacing: 0, marginLeft: 8 }}>{row.formula}</span>
          </div>
          <div className="lbl">=</div>
          <div className="v">{fmt(row.value)}</div>
          <div></div>
          <div></div>
        </div>
      )}
    </div>
  );
}

interface RowProps {
  row: ReportRow;
  expanded: boolean;
  onToggle: (uid: string) => void;
  onSelect: (uid: string) => void;
  selected: boolean;
}

function Row({ row, expanded, onToggle, onSelect, selected }: RowProps) {
  const diff = row.master !== null ? row.value - (row.master as number) : null;
  const p = diff !== null && row.master ? (diff / Math.abs(row.master as number)) * 100 : null;

  const statusCls =
    row.status === 'match' ? 's-match' :
    row.status === 'diff'  ? 's-diff' :
    row.status === 'over'  ? 's-over' : '';
  const isTotal = row.level === 0 && /итого|чистая|валовая|прибыль до/i.test(row.name);
  const indent = row.level * 14;

  return (
    <>
      <div
        id={`r-${row.uid}`}
        className={`row ${statusCls} ${selected ? 'selected' : ''}`}
        onClick={() => onSelect(row.uid)}
        style={isTotal ? { fontWeight: 500, background: 'var(--bg-elev)' } : undefined}
      >
        <div className="c-tw">
          <span className={`tw-dot ${row.status === 'match' ? 'match' : row.status === 'diff' ? 'diff' : row.status === 'over' ? 'over' : ''}`} />
        </div>
        <div className="c-name" style={{ paddingLeft: 10 + indent }}>
          <span className={row.level === 0 ? 'lvl0' : ''}>{row.name.trim()}</span>
          {row.comment && (
            <span className="has-comment on" title={row.comment.text}>✎</span>
          )}
          {row.note && row.status === 'over' && (
            <span title={row.note} style={{ marginLeft: 6, fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--override)', border: '1px solid var(--override)', borderRadius: 2, padding: '0 4px', lineHeight: '14px' }}>NOTE</span>
          )}
        </div>
        <div className="c-num">{fmt(row.value)}</div>
        <div className="c-master">{row.master === null ? '—' : fmt(row.master)}</div>
        <div className="c-diff">
          {diff === null ? <span style={{ color: 'var(--ink-4)' }}>—</span>
            : diff === 0 ? <span style={{ color: 'var(--match)' }}>✓ 0</span>
            : <>{fmt(diff)}<span className="pct">{p === null || !isFinite(p) ? 'new' : (p > 0 ? '+' : '') + p.toFixed(2) + '%'}</span></>}
        </div>
        <div className="c-act">
          <button className="expand" onClick={(e) => { e.stopPropagation(); onToggle(row.uid); }}>
            {expanded ? '−' : '+'}
          </button>
        </div>
      </div>
      {expanded && <Tree row={row} />}
    </>
  );
}

interface TableProps {
  table: ReportTable;
  expanded: Set<string>;
  setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>;
  selected: string | null;
  setSelected: (uid: string) => void;
  filteredIds: Set<string>;
}

function Table({ table, expanded, setExpanded, selected, setSelected, filteredIds }: TableProps) {
  const visibleRows = table.rows.filter((r) => filteredIds.has(r.uid));
  if (visibleRows.length === 0) return null;
  return (
    <div className="table">
      <div className="tcap">
        <span className="num">{table.num}</span>
        <span className="name">{table.name}</span>
        <span className="meta">
          <span className="k">rows </span>{visibleRows.length}<span className="k">/{table.rows.length}</span>
        </span>
      </div>
      <div className="thead">
        <div></div>
        <div>Статья</div>
        <div className="n">Расчёт</div>
        <div className="n">Master</div>
        <div className="n">Δ Расхождение</div>
        <div></div>
      </div>
      {visibleRows.map((row) => (
        <Row
          key={row.uid}
          row={row}
          expanded={expanded.has(row.uid)}
          onToggle={(id) => setExpanded((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
          })}
          onSelect={setSelected}
          selected={selected === row.uid}
        />
      ))}
    </div>
  );
}

function StatusBar({ counts, filter, selectedRow }: { counts: Counts; filter: string; selectedRow: string | null }) {
  return (
    <div className="statusbar">
      <span>● connected</span>
      <span className="sep">·</span>
      <span>api/v1/clusters/dd_1quart_2026</span>
      <span className="sep">·</span>
      <span>filter: <b style={{ color: 'var(--ink-2)' }}>{filter}</b></span>
      {selectedRow && <>
        <span className="sep">·</span>
        <span>row {selectedRow}</span>
      </>}
      <span className="right">
        <span>● {counts.match} match</span>
        <span style={{ color: 'var(--diff)' }}>● {counts.diff} diff</span>
        <span style={{ color: 'var(--override)' }}>● {counts.over} override</span>
        <span className="sep">·</span>
        <span>v 0.4.2</span>
      </span>
    </div>
  );
}

export default function Reports() {
  const [t, setTweak] = useTweaks(mergeDefaults(TWEAK_DEFAULTS));

  const [filter, setFilter] = useState<'all' | RowStatus>('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['sgd-5', 'adm_1c-12']));
  const [selected, setSelected] = useState<string | null>('sgd-5');

  const filteredIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    const ids = new Set<string>();
    ALL_ROWS.forEach((r) => {
      if (filter !== 'all' && r.status !== filter) return;
      if (q && !r.name.toLowerCase().includes(q)) return;
      ids.add(r.uid);
    });
    return ids;
  }, [filter, query]);

  const counts = COUNTS;

  const selectedRow = useMemo(
    () => ALL_ROWS.find((r) => r.uid === selected),
    [selected]
  );

  const onJumpFirstDiff = useCallback(() => {
    const first = ALL_ROWS.find((r) => r.status === 'diff');
    if (first) {
      setSelected(first.uid);
      setExpanded((s) => {
        const n = new Set(s);
        n.add(first.uid);
        return n;
      });
      const el = document.getElementById(`r-${first.uid}`);
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, []);

  const onJumpComment = useCallback((uid: string) => {
    setSelected(uid);
    const el = document.getElementById(`r-${uid}`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  return (
    <div className="shell" data-theme={t.theme} data-density={t.density} data-variant={t.variant}>
      <NavRail />
      <div
        className="app"
        data-theme={t.theme}
        data-density={t.density}
        data-variant={t.variant}
        data-comments={t.showComments ? '1' : '0'}
        data-audit={t.showAudit ? '1' : '0'}
      >
        <TitleBar total={ALL_ROWS.length} />
        <Switcher />
        <DiffBar
          filter={filter} setFilter={setFilter}
          counts={counts}
          query={query} setQuery={setQuery}
          onJumpFirstDiff={onJumpFirstDiff}
        />
        <div className="body">
          <div className="main">
            <div className="tablewrap">
              {TABLES.map((tbl) => (
                <Table
                  key={tbl.id}
                  table={tbl}
                  expanded={expanded} setExpanded={setExpanded}
                  selected={selected} setSelected={setSelected}
                  filteredIds={filteredIds}
                />
              ))}
            </div>
          </div>
          {t.showComments && (
            <CommentsRail rows={ALL_ROWS} activeUid={selected} onJump={onJumpComment} />
          )}
          {t.showAudit && (
            <AuditRail row={selectedRow} />
          )}
        </div>
        <StatusBar counts={counts} filter={filter} selectedRow={selected} />

        <TweaksPanel>
          <TweakSection label="Тема" />
          <TweakRadio label="Theme" value={t.theme}
            options={['paper', 'light', 'dark']}
            onChange={(v) => setTweak('theme', v)} />
          <TweakSection label="Вариант" />
          <TweakRadio label="Variant" value={t.variant}
            options={['safe', 'bold']}
            onChange={(v) => setTweak('variant', v)} />
          <TweakSection label="Плотность" />
          <TweakRadio label="Density" value={t.density}
            options={['compact', 'regular', 'comfy']}
            onChange={(v) => setTweak('density', v)} />
          <TweakSection label="Панели" />
          <TweakToggle label="Комментарии (правая панель)" value={t.showComments}
            onChange={(v) => setTweak('showComments', v)} />
          <TweakToggle label="Audit trail (правая панель)" value={t.showAudit}
            onChange={(v) => setTweak('showAudit', v)} />
        </TweaksPanel>
      </div>
    </div>
  );
}
